import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { geoCentroid } from 'd3-geo';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import {
  type DataEntryMode,
  DEFAULT_REGION_GREY,
  formatOverlayCellValue,
  parseChoroplethTable,
  scanEasyTableRange,
  type ChoroplethTableParse,
} from '../utils/naRegionResolve';

const US_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const CA_GEO_URL =
  'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson';

/** Larger canvas so the map stays crisp when stretched full-width below the table. */
const MAP_BASE_W = 900;
const MAP_BASE_SCALE = 420;
const MAP_WIDTH = 1280;
const MAP_HEIGHT = Math.round((MAP_WIDTH * 520) / MAP_BASE_W);
const MAP_SCALE = Math.round((MAP_BASE_SCALE * MAP_WIDTH) / MAP_BASE_W);

function regionFill(
  geo: { properties?: { name?: string } },
  frameColors: Record<string, string> | undefined
): string {
  const name = geo.properties?.name;
  if (!name) return DEFAULT_REGION_GREY;
  return frameColors?.[name] ?? DEFAULT_REGION_GREY;
}

function safeCentroid(geo: { geometry?: unknown }): [number, number] | null {
  try {
    const c = geoCentroid(geo as any);
    if (Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
      return [c[0], c[1]];
    }
  } catch {
    /* ignore invalid geometries */
  }
  return null;
}

export function AnimatedRegionMapPage() {
  const [dataEntryMode, setDataEntryMode] = useState<DataEntryMode>('flexible');
  const [easyColorRange, setEasyColorRange] = useState<'rgb' | 'bpr'>('rgb');
  const [easyMin, setEasyMin] = useState(0);
  const [easyMax, setEasyMax] = useState(1000);
  const [scaleFactor, setScaleFactor] = useState(1000);
  const [inputText, setInputText] = useState('');
  const [parsed, setParsed] = useState<ChoroplethTableParse | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [frameMs, setFrameMs] = useState(900);
  const [backgroundColor, setBackgroundColor] = useState('#f4f4f4');
  const [mapCenter, setMapCenter] = useState<[number, number]>([-100, 52]);
  const [mapZoom, setMapZoom] = useState(1);
  const [showRegionOverlay, setShowRegionOverlay] = useState(false);

  const frameCount = parsed?.frames.length ?? 0;
  const activeFrame = frameCount > 0 ? parsed!.frames[Math.min(frameIndex, frameCount - 1)] : undefined;
  const activeRawFrame =
    frameCount > 0 && parsed
      ? parsed.rawFrameCells[Math.min(frameIndex, parsed.rawFrameCells.length - 1)]
      : undefined;
  const frameLabel =
    frameCount > 0 ? parsed!.frameLabels[Math.min(frameIndex, frameCount - 1)] ?? `Frame ${frameIndex + 1}` : '';

  useEffect(() => {
    if (!isPlaying || frameCount <= 1) return;
    const id = window.setInterval(() => {
      setFrameIndex((i) => (i + 1) % frameCount);
    }, frameMs);
    return () => window.clearInterval(id);
  }, [isPlaying, frameCount, frameMs]);

  const handleParseAndPreview = useCallback(() => {
    setParseError(null);
    const t = inputText.trim();
    if (!t) {
      setParseError(
        dataEntryMode === 'easy'
          ? `Paste a table: column 1 = state/province, then numeric value columns per frame (expected ${easyMin}–${easyMax}).`
          : 'Paste a table: column 1 = state/province, then one color column per frame.'
      );
      setParsed(null);
      return;
    }
    try {
      const result = parseChoroplethTable(t, {
        mode: dataEntryMode,
        easy:
          dataEntryMode === 'easy'
            ? { min: easyMin, max: easyMax, scaleFactor, colorRange: easyColorRange }
            : undefined,
      });
      if (result.frames.length === 0) {
        setParseError(
          dataEntryMode === 'easy'
            ? 'No value columns found. Add at least one numeric value after the region name.'
            : 'No color columns found. Add at least one color after the region name (e.g. #ff0000 or hsl(0, 100%, 50%)).'
        );
        setParsed(null);
        return;
      }
      setParsed(result);
      setFrameIndex(0);
      setIsPlaying(false);
      setMapCenter([-100, 52]);
      setMapZoom(1);
    } catch (e) {
      setParsed(null);
      setParseError(e instanceof Error ? e.message : 'Could not parse table.');
    }
  }, [inputText, dataEntryMode, easyMin, easyMax, scaleFactor, easyColorRange]);

  const mapStyle = useMemo(
    () => ({
      default: { stroke: '#ffffff', strokeWidth: 0.35, outline: 'none' as const },
      hover: { stroke: '#222222', strokeWidth: 0.6, outline: 'none' as const },
      pressed: { stroke: '#222222', strokeWidth: 0.6, outline: 'none' as const },
    }),
    []
  );

  return (
    <div style={{ maxWidth: 'min(100%, 1680px)', margin: '0 auto', padding: '0 1.5rem 2.5rem' }}>
      <h1 style={{ color: '#213547', marginBottom: '0.5rem' }}>Animated US &amp; Canada region map</h1>
      <p style={{ color: '#555', maxWidth: '720px', lineHeight: 1.5 }}>
        Paste a table copied from Excel or Google Sheets: first column is a US state / territory or Canadian
        province or territory (names or common abbreviations). In <b>Easy mode</b>, each following column is a
        number mapped to a color ramp. In <b>Flexible mode</b>, each following
        column is a fill color: <code>#RRGGBB</code>, <code>#RGB</code>, or CSS <code>hsl()</code> / <code>hsla()</code>{' '}
        (comma form or space form with optional <code>/</code> alpha). Regions not listed use light grey.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.5rem',
          alignItems: 'stretch',
          marginTop: '1.25rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 600, color: '#333' }}>Data Entry Mode:</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#333' }}>
              <input
                type="radio"
                name="dataEntryMode"
                value="easy"
                checked={dataEntryMode === 'easy'}
                onChange={() => {
                  setDataEntryMode('easy');
                  setParsed(null);
                  setIsPlaying(false);
                  setFrameIndex(0);
                }}
              />
              Easy mode (Scale 0 to 1000)
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#333' }}>
              <input
                type="radio"
                name="dataEntryMode"
                value="flexible"
                checked={dataEntryMode === 'flexible'}
                onChange={() => {
                  setDataEntryMode('flexible');
                  setParsed(null);
                  setIsPlaying(false);
                  setFrameIndex(0);
                }}
              />
              Flexible Mode (Enter any color)
            </label>
          </div>
          {dataEntryMode === 'easy' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              <button
                type="button"
                onClick={() => {
                  const range = scanEasyTableRange(inputText);
                  if (!range) {
                    setParseError('Autoscale: could not find any numeric values in the table.');
                    return;
                  }
                  const min = range.min;
                  const max = range.max;
                  const sf = max - min;
                  setEasyMin(min);
                  setEasyMax(max);
                  setScaleFactor(sf > 0 ? sf : 1);
                  setParsed(null);
                  setIsPlaying(false);
                  setFrameIndex(0);
                }}
              >
                Autoscale
              </button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#333', fontWeight: 600 }}>
                Color range
                <select
                  value={easyColorRange}
                  onChange={(e) => {
                    setEasyColorRange(e.target.value as 'rgb' | 'bpr');
                    setParsed(null);
                    setIsPlaying(false);
                    setFrameIndex(0);
                  }}
                  style={{ padding: '0.25rem 0.4rem' }}
                >
                  <option value="rgb">Red-Green-Blue</option>
                  <option value="bpr">Blue-Purple-Red</option>
                </select>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#333', fontWeight: 600 }}>
                Min
                <input
                  type="number"
                  step="any"
                  value={easyMin}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setEasyMin(n);
                    setParsed(null);
                    setIsPlaying(false);
                    setFrameIndex(0);
                  }}
                  style={{ width: '7rem' }}
                />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#333', fontWeight: 600 }}>
                Max
                <input
                  type="number"
                  step="any"
                  value={easyMax}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setEasyMax(n);
                    setParsed(null);
                    setIsPlaying(false);
                    setFrameIndex(0);
                  }}
                  style={{ width: '7rem' }}
                />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#333', fontWeight: 600 }}>
                Scale Factor
                <input
                  type="number"
                  min={1}
                  step="any"
                  value={scaleFactor}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setScaleFactor(Number.isFinite(n) && n > 0 ? n : 1000);
                    setParsed(null);
                    setIsPlaying(false);
                    setFrameIndex(0);
                  }}
                  style={{ width: '7rem' }}
                />
              </label>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                {easyColorRange === 'bpr' ? (
                  <>
                    Values map to <code>hsl(230 + 130*(x-min)/scaleFactor, 67%, 56%)</code>
                  </>
                ) : (
                  <>
                    Values map to <code>hsl(256*(x-min)/scaleFactor, 67%, 56%)</code>
                  </>
                )}
              </span>
            </div>
          )}
          <label htmlFor="region-table-input" style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', color: '#333' }}>
            Data table
          </label>
          <textarea
            id="region-table-input"
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
            }}
            wrap="off"
            placeholder={
              dataEntryMode === 'easy'
                ? `State\tFrame 1\tFrame 2\nCalifornia\t${easyMin}\t${easyMax}\nTexas\t${easyMin + (easyMax - easyMin) * 0.2}\t${
                    easyMin + (easyMax - easyMin) * 0.8
                  }\nON\t${easyMin + (easyMax - easyMin) * 0.5}\t${easyMin + (easyMax - easyMin) * 0.65}`
                : 'State\tFrame 1\tFrame 2\nCalifornia\t#2a6fdb\thsl(200, 70%, 45%)\nTexas\thsl(25 90% 55%)\t#2a9d8f\nON\thsla(180, 40%, 40%, 0.85)\t#e9c46a'
            }
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: '220px',
              boxSizing: 'border-box',
              fontFamily: 'ui-monospace, Consolas, monospace',
              fontSize: '13px',
              padding: '0.65rem',
              borderRadius: '8px',
              border: '1px solid #ccc',
              resize: 'vertical',
              overflowX: 'auto',
              whiteSpace: 'pre',
              wordBreak: 'normal',
            }}
          />
          {parseError && (
            <p style={{ color: '#b00020', marginTop: '0.5rem', fontSize: '0.9rem' }}>{parseError}</p>
          )}
          <button type="button" onClick={handleParseAndPreview} style={{ marginTop: '0.75rem' }}>
            Load map
          </button>
          {parsed && parsed.unmatchedLabels.length > 0 && (
            <p style={{ color: '#856404', marginTop: '0.75rem', fontSize: '0.88rem' }}>
              Unrecognized region labels (not colored): {parsed.unmatchedLabels.join(', ')}
            </p>
          )}
        </div>

        <div
          style={{
            backgroundColor,
            borderRadius: '12px',
            padding: '1rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            width: '100%',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 600, color: '#333' }}>
              {frameCount > 0 ? (
                <>
                  {frameLabel} <span style={{ fontWeight: 400, color: '#666' }}>({frameIndex + 1} / {frameCount})</span>
                </>
              ) : (
                'Load data to animate'
              )}
            </span>
            <button
              type="button"
              onClick={() => setMapZoom((z) => Math.min(8, Math.round((z + 0.25) * 100) / 100))}
            >
              Zoom +
            </button>
            <button
              type="button"
              onClick={() => setMapZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))}
            >
              Zoom -
            </button>
            <button
              type="button"
              onClick={() => {
                setMapCenter([-100, 52]);
                setMapZoom(1);
              }}
            >
              Reset view
            </button>
            <button
              type="button"
              disabled={frameCount <= 1}
              onClick={() => setIsPlaying((p) => !p)}
              style={{ opacity: frameCount <= 1 ? 0.5 : 1 }}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              disabled={frameCount === 0}
              onClick={() => {
                setFrameIndex((i) => (i - 1 + frameCount) % frameCount);
              }}
            >
              Prev
            </button>
            <button
              type="button"
              disabled={frameCount === 0}
              onClick={() => {
                setFrameIndex((i) => (i + 1) % frameCount);
              }}
            >
              Next
            </button>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#444', fontSize: '0.9rem' }}>
              ms / frame
              <input
                type="number"
                min={200}
                max={5000}
                step={100}
                value={frameMs}
                onChange={(e) => setFrameMs(Number(e.target.value) || 900)}
                style={{ width: '5rem' }}
              />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#444', fontSize: '0.9rem' }}>
              Map bg
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
              />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#444', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={showRegionOverlay}
                onChange={(e) => setShowRegionOverlay(e.target.checked)}
              />
              Show values on map
            </label>
          </div>
          {frameCount > 0 && (
            <input
              type="range"
              min={0}
              max={frameCount - 1}
              value={Math.min(frameIndex, frameCount - 1)}
              onChange={(e) => setFrameIndex(Number(e.target.value))}
              style={{ width: '100%', marginBottom: '0.75rem' }}
            />
          )}

          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: MAP_SCALE, center: [-100, 52] }}
            width={MAP_WIDTH}
            height={MAP_HEIGHT}
            style={{ width: '100%', height: 'auto', maxWidth: '100%', display: 'block' }}
          >
            <g pointerEvents="none" aria-hidden="true">
              {[
                [MAP_WIDTH * 0.2, MAP_HEIGHT * 0.33],
                [MAP_WIDTH * 0.5, MAP_HEIGHT * 0.33],
                [MAP_WIDTH * 0.8, MAP_HEIGHT * 0.33],
                [MAP_WIDTH * 0.2, MAP_HEIGHT * 0.72],
                [MAP_WIDTH * 0.5, MAP_HEIGHT * 0.72],
                [MAP_WIDTH * 0.8, MAP_HEIGHT * 0.72],
              ].map(([x, y], i) => (
                <text
                  key={i}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(-25 ${x} ${y})`}
                  fill="rgba(0,0,0,0.16)"
                  style={{
                    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                    fontWeight: 700,
                    fontSize: `${Math.round(MAP_WIDTH / 16)}px`,
                    letterSpacing: '0.05em',
                    userSelect: 'none',
                  }}
                >
                  graffy.ca
                </text>
              ))}
            </g>
            {frameLabel && (
              <g pointerEvents="none" aria-hidden="true">
                <rect
                  x={MAP_WIDTH - 16 - 320}
                  y={MAP_HEIGHT - 16 - 44}
                  width={320}
                  height={44}
                  rx={10}
                  fill="rgba(255,255,255,0.65)"
                />
                <text
                  x={MAP_WIDTH - 16}
                  y={MAP_HEIGHT - 16 - 22}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="rgba(0,0,0,0.70)"
                  style={{
                    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                    fontWeight: 700,
                    fontSize: '22px',
                    letterSpacing: '0.01em',
                    userSelect: 'none',
                  }}
                >
                  {frameLabel}
                </text>
              </g>
            )}
            <ZoomableGroup
              center={mapCenter}
              zoom={mapZoom}
              minZoom={1}
              maxZoom={8}
              onMoveEnd={({ coordinates, zoom }: { coordinates: [number, number]; zoom: number }) => {
                setMapCenter(coordinates);
                setMapZoom(zoom);
              }}
            >
              <Geographies geography={US_GEO_URL}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const fill = regionFill(geo, activeFrame);
                    const name = geo.properties?.name as string | undefined;
                    const raw = name && activeRawFrame ? activeRawFrame[name] : undefined;
                    const overlayText =
                      showRegionOverlay && raw ? formatOverlayCellValue(raw, dataEntryMode) : null;
                    const centroid = overlayText ? safeCentroid(geo) : null;
                    return (
                      <Fragment key={geo.rsmKey}>
                        <Geography
                          geography={geo}
                          style={{
                            default: { ...mapStyle.default, fill },
                            hover: { ...mapStyle.hover, fill },
                            pressed: { ...mapStyle.pressed, fill },
                          }}
                        />
                        {overlayText && centroid && (
                          <Marker coordinates={centroid}>
                            <text
                              textAnchor="middle"
                              y={2}
                              pointerEvents="none"
                              fill="#111111"
                              stroke="rgba(255,255,255,0.88)"
                              strokeWidth={2.5}
                              paintOrder="stroke"
                              style={{
                                fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                                fontSize: '11px',
                                fontWeight: 700,
                                userSelect: 'none',
                              }}
                            >
                              {overlayText}
                            </text>
                          </Marker>
                        )}
                      </Fragment>
                    );
                  })
                }
              </Geographies>
              <Geographies geography={CA_GEO_URL}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const fill = regionFill(geo, activeFrame);
                    const name = geo.properties?.name as string | undefined;
                    const raw = name && activeRawFrame ? activeRawFrame[name] : undefined;
                    const overlayText =
                      showRegionOverlay && raw ? formatOverlayCellValue(raw, dataEntryMode) : null;
                    const centroid = overlayText ? safeCentroid(geo) : null;
                    return (
                      <Fragment key={geo.rsmKey}>
                        <Geography
                          geography={geo}
                          style={{
                            default: { ...mapStyle.default, fill },
                            hover: { ...mapStyle.hover, fill },
                            pressed: { ...mapStyle.pressed, fill },
                          }}
                        />
                        {overlayText && centroid && (
                          <Marker coordinates={centroid}>
                            <text
                              textAnchor="middle"
                              y={2}
                              pointerEvents="none"
                              fill="#111111"
                              stroke="rgba(255,255,255,0.88)"
                              strokeWidth={2.5}
                              paintOrder="stroke"
                              style={{
                                fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                                fontSize: '11px',
                                fontWeight: 700,
                                userSelect: 'none',
                              }}
                            >
                              {overlayText}
                            </text>
                          </Marker>
                        )}
                      </Fragment>
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
        </div>
      </div>
    </div>
  );
}
